import {tealiumTracker} from '@src/Services/Tealium';
import type {
  TealiumAuthProps,
  TealiumPageViewProps,
} from '@src/Services/Tealium';
import type {
  TrackingAdapter,
  TrackingEventName,
  TrackingPayload,
} from '../TrackingContext';

const isPageViewProps = (
  props?: TrackingPayload,
): props is TealiumPageViewProps =>
  !!props && typeof props.path === 'string';

const toPagePath = (props?: TrackingPayload): string =>
  typeof props?.path === 'string'
    ? props.path
    : typeof window !== 'undefined'
      ? window.location.pathname
      : '/';

const trackTealiumEvent = (
  event: TrackingEventName,
  props?: TrackingPayload,
): void => {
  switch (event) {
    case 'page.view':
      if (isPageViewProps(props)) {
        tealiumTracker.view(props);
        return;
      }
      tealiumTracker.view({path: toPagePath(props)});
      return;

    // Canonical SPA page-view event (see LayoutProvider.tsx). Amplitude-shaped
    // props (page_name/page_group) aren't part of the Tealium schema, so only
    // the path is forwarded — buildBaseDataLayer derives Tealium's own
    // page_name/page_section from it instead of inheriting Amplitude's.
    case 'page_viewed':
      tealiumTracker.view({path: toPagePath(props)});
      return;

    case 'auth.login_successful':
      tealiumTracker.auth.loginSuccessful(
        toPagePath(props),
        props as TealiumAuthProps,
      );
      return;

    case 'auth.signup_successful':
      tealiumTracker.auth.signupSuccessful(
        toPagePath(props),
        props as TealiumAuthProps,
      );
      return;

    case 'auth.signout':
      tealiumTracker.auth.signout(
        toPagePath(props),
        props?.pageSection as string | undefined,
      );
      return;

    default:
      tealiumTracker.link(event, {
        path: toPagePath(props),
        extra: props,
      });
  }
};

export const createTealiumAdapter = (): TrackingAdapter => ({
  name: 'tealium',
  receivesAll: true,
  track: trackTealiumEvent,
});
